import moment from 'moment';
import {
  Crew,
  SlotEvent,
  PaxLoad,
  ActualPaxLoad,
  Transfer,
  Cargo,
  Ramp,
  Task,
  FixedRamp,
} from 'model/types';
import * as utils from './utils';
import * as dates from './dates';

export const SCHEDULE_SUMMER = 100;
export const SCHEDULE_WINTER = 200;

export const PARKING_STAND_TYPES = [
  { name: 'Remote stand', value: 'remote' },
  { name: 'Contact stand', value: 'contact' },
];

export const CREW_CHANGE_TYPES = [
  { name: 'Back to back crew', value: 'backToBack' },
  { name: 'Crew Change', value: 'change' },
];

/**
 * Calculates sum of all values in array or returns null
 * @param values
 */
export const calcArrayTotal = (
  values: (number | string)[] = []
): number | null => {
  const result: number = values
    .filter((value): value is number => Number.isFinite(value))
    .reduce((total: number | null, value: number) => total + value, null);
  return result === null ? result : utils.fixFloat(result);
};

/**
 * Calculates sum of all fields in the object or returns null
 * @param obj
 */
export const calcObjectTotal = (
  obj: Record<string, number> = {}
): number | null => calcArrayTotal(Object.values(obj));

/**
 * Calculates sum of specified property of all objects in array or returns null
 * @param iterableObject
 * @param key
 */
export const calcArrayPropertyTotal = <
  T extends Record<string, number | string>,
>(
  iterableObject: T[],
  key: keyof T
): number | null => calcArrayTotal(iterableObject.map(value => value[key]));

export const getSeasonSchedule = (date: string): number => {
  const month = moment(date).month();
  return month < 4 || month > 10 ? SCHEDULE_WINTER : SCHEDULE_SUMMER;
};

export const sortEvents = (events: SlotEvent[]): SlotEvent[] =>
  [...events].sort(utils.makeCompareStrings('STD'));

export const getAdultWeight = (seasonSchedule: number): number =>
  seasonSchedule === SCHEDULE_WINTER ? 80 : 75;

export const getChildWeight = (): number => 30;

export const getInfantWeight = (): number => 15;

/**
 * Returns value or null
 * @param crew
 */
export const getTotalCrewCount = (crew: Crew = {}): number | null =>
  calcObjectTotal(crew);

/**
 * Returns operational crew count or     null
 * @param crew
 */
export const getOpsCrewCount = (crew: Crew = {}): number | null => {
  return Number.isFinite(crew.pilots) || Number.isFinite(crew.cabin)
    ? (crew.pilots ?? 0) + (crew.cabin ?? 0)
    : null;
};

/**
 * Returns dead head crew count or null
 * @param crew
 * @returns {*}
 */
export const getDeadHeadCrewCount = (crew: Crew = {}): number | null => {
  return Number.isFinite(crew.deadhead) ? crew.deadhead! : null;
};

/**
 * Returns total pax load or null
 * @param paxLoad
 * @returns {unknown}
 */
export const getTotalPaxLoad = (paxLoad: PaxLoad = {}): number | null => {
  const values = {
    first: paxLoad.first,
    business: paxLoad.business,
    economy: paxLoad.economy,
    infant: paxLoad.infant,
  };
  return calcObjectTotal(values);
};

export const getTotalAdultsCount = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const adults = {
    firstMales: details.first?.male,
    firstFemales: details.first?.female,
    businessMales: details.business?.male,
    businessFemales: details.business?.female,
    economyMales: details.economy?.male,
    economyFemales: details.economy?.female,
  };

  return calcObjectTotal(adults);
};

export const getTotalChildrenCount = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const children = {
    firstChildren: details.first?.child,
    businessChildren: details.business?.child,
    economyChildren: details.economy?.child,
  };

  return calcObjectTotal(children);
};

export const getTotalInfantCount = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const infants = {
    firstInfant: details.first?.infant,
    businessInfant: details.business?.infant,
    economyInfant: details.economy?.infant,
  };

  return calcObjectTotal(infants);
};

export const getTotalPaxWeight = (
  actualTime: string,
  paxLoad: PaxLoad = {}
): number | null => {
  const adultsCount = getTotalAdultsCount(paxLoad);
  const childrenCount = getTotalChildrenCount(paxLoad);
  const infantsCount = getTotalInfantCount(paxLoad);

  const adultWeight = getAdultWeight(getSeasonSchedule(actualTime));

  const weight = {
    adults: adultsCount ? adultsCount * adultWeight : adultsCount,
    children: childrenCount ? childrenCount * getChildWeight() : childrenCount,
    infants: infantsCount ? infantsCount * getInfantWeight() : infantsCount,
  };

  return calcObjectTotal(weight);
};

export const getTotalBagsWeight = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const bags = {
    first: details.first?.baggage?.weight ?? 0,
    business: details.business?.baggage?.weight ?? 0,
    economy: details.economy?.baggage?.weight ?? 0,
  };

  return calcObjectTotal(bags);
};

export const getTotalHandbagsWeight = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const bags = {
    first: details.first?.baggage?.handCarry,
    business: details.business?.baggage?.handCarry,
    economy: details.economy?.baggage?.handCarry,
  };

  return calcObjectTotal(bags);
};

export const getTotalBagsCount = (
  paxLoad: ActualPaxLoad = {}
): number | null => {
  const { details } = paxLoad;
  if (!details) return null;

  const bags = {
    first: details.first?.baggage?.pkgs,
    business: details.business?.baggage?.pkgs,
    economy: details.economy?.baggage?.pkgs,
  };

  return calcObjectTotal(bags);
};

export function getTotalPaxLoadByClass(
  paxLoad: ActualPaxLoad = {},
  acClassName: 'first' | 'business' | 'economy'
): number | null {
  const pax = paxLoad.details?.[acClassName];
  return calcObjectTotal({
    male: pax?.male,
    female: pax?.female,
    child: pax?.child,
  });
}

export function getPaxLoadSummary(paxLoad: PaxLoad = {}) {
  return {
    first: getTotalPaxLoadByClass(paxLoad, 'first'),
    business: getTotalPaxLoadByClass(paxLoad, 'business'),
    economy: getTotalPaxLoadByClass(paxLoad, 'economy'),
    infant: getTotalInfantCount(paxLoad),
  };
}

// TODO transfer and transit for outbound flight will be separate
export const getTransferCount = (transfers: Transfer[]): number | null => {
  return calcArrayPropertyTotal(
    transfers.map(value => ({
      total: calcObjectTotal({
        first: value.first,
        business: value.business,
        economy: value.economy,
      }),
    })),
    'total'
  );
};

export const getTotalCargoWeight = (cargo: Cargo[]): number | null =>
  calcArrayPropertyTotal(cargo, 'weight');

export const getPayload = (
  actualTime: string,
  paxLoad: PaxLoad,
  cargo: Cargo[]
) => {
  const paxWeight = getTotalPaxWeight(actualTime, paxLoad);
  const bagsWeight = getTotalBagsWeight(paxLoad);
  const handbagsWeight = getTotalHandbagsWeight(paxLoad);
  const cargoWeight = getTotalCargoWeight(cargo);

  return {
    paxWeight,
    bagsWeight,
    handbagsWeight,
    cargoWeight,
    payload: paxWeight + bagsWeight + handbagsWeight + cargoWeight || null,
  };
};

export const getTimeDifferenceData = (
  startDate: string | Date | moment.Moment,
  endDate: string | Date | moment.Moment
): {
  difference: number;
  duration: moment.Duration;
  sign: number;
  hours: number;
  minutes: number;
} => {
  const difference = moment(endDate).diff(startDate);
  const duration = moment.duration(Math.abs(difference));

  let sign = Math.sign(difference);
  const hours = Math.trunc(duration.asHours());
  const minutes = duration.minutes();
  sign = hours === 0 && minutes === 0 ? 1 : sign;
  return {
    difference,
    duration,
    sign,
    hours,
    minutes,
  };
};

export const calculateDelayTime = (
  startDate: string | Date | moment.Moment,
  endDate: string | Date | moment.Moment
): [boolean, string, number] => {
  const { sign, difference, hours, minutes } = getTimeDifferenceData(
    startDate,
    endDate
  );

  // Delayed if more than 15 mins
  return [
    difference > 900000,
    `${sign < 0 ? '-' : ''}${hours < 10 ? '0' : ''}${hours}:${
      minutes < 10 ? '0' : ''
    }${minutes}`,
    difference,
  ];
};

export const isUralAirlines = (event: SlotEvent): boolean =>
  event.airline === 'U6';

export const isPobedaAirlines = (event: SlotEvent): boolean =>
  event.airline === 'DP';

/**
 * Return ramp service for the event based on ramp service name
 * @param ramp
 * @param fixedRamp
 * @param id
 * @returns {*}
 */
export const getRampServiceById = (
  ramp: Ramp[],
  fixedRamp: Record<string, FixedRamp> | undefined,
  id: string
): Ramp | FixedRamp | undefined => {
  return ramp.find(item => item.rampId === id) || fixedRamp?.[id];
};

export const isReportSubmitted = (event: SlotEvent): boolean =>
  !!event.outbound.report?.id;

export const getUncompletedTasks = (tasks: Task[] | undefined): Task[] =>
  tasks?.filter(task => !task.isCompleted) ?? [];

export const getCompletedTasks = (tasks: Task[] | undefined): Task[] =>
  tasks?.filter(task => task.isCompleted) ?? [];

/**
 * Add auxiliary pre-computed fields to the event object.
 * @param event
 * @returns {*&{start: Date, end: Date, title: string, isSameDay: boolean, flightNumber: string}}
 */
export const refineEvent = event => {
  const timeFormat = date => date.format('HHmm');

  const start = moment(event.start);
  const end = moment(event.end);
  const isSameDay = start.day() === end.day();
  const flightNumber = `${event.airline}-${event.inboundNumber}/${event.outboundNumber}`;

  const title = `${timeFormat(start)} ${flightNumber} ${timeFormat(
    moment(event.STA)
  )}-${timeFormat(end)} ${event.inboundAirport}-${event.airport}-${
    event.outboundAirport
  }`;

  return {
    ...event,
    title,
    start: start.toDate(),
    end: end.toDate(),
    isSameDay,
    flightNumber,
  };
};

/**
 * Sanitizes event by removing additional computed fields. Opposite of
 * `refineEvent()` method.
 */
export const sanitizeEvent = event => {
  const sanitizedEvent = {
    ...event,
    start: dates.dateToUtcDateTimeString(event.start),
    end: dates.dateToUtcDateTimeString(event.end),
  };
  delete sanitizedEvent.title;
  delete sanitizedEvent.isSameDay;
  delete sanitizedEvent.flightNumber;
  return sanitizedEvent;
};

export const getTaskItemTitle = (description: string): string =>
  description.trimStart().split('\n')[0];
